import { useCache, useList, useSubscribeOnWsUpdate, useTriggerAction, useUpdateWidgetValue } from '@libs/hooks';
import { ActionActionTypeEnum, AssetsSvgMap, TItem, TWAction, TWidget, TWidgetProps } from '@libs/types';
import { ConditionalRender, eventBus } from '@libs/utils';
import clsx from 'clsx';
import React, { useCallback, useEffect, useEffectEvent, useRef, useState } from 'react';
import { SpinnerCircle } from '../UI/Spinner/SpinnerCircle';
import { SvgComponent } from '../../common/Svg/SvgComponent';
import { TConsumerItem, TConsumerListData, TListDataMessages } from '../../types/data';
import { TListOption } from '../../types/options';
import { getInlineStyles } from '../../utils/styles';
import { TextCommon } from '../UI/Text/TextCommon';
import ListTransactionItems from './ListTransactionItems';
import styles from './styles/styles.module.css';
import { convertTransactionItems, mergeArrays } from './utils/transactionUtils';

export const ListTransactions: React.FC<TWidgetProps<TConsumerListData, TListOption>> = ({ widget }) => {
    const triggerRef = useRef(null);
    const fetchingData = useRef(false);
    const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const { getCache, putCache } = useCache(widget);

    const actions = widget.actions;
    const margin = widget.options?.margin;
    const data = widget.data ?? {};
    const paging = data.settings?.paging ?? {};
    const messages = data.messages ?? ({} as TListDataMessages);

    const [items, setItems] = useState<TConsumerItem[]>(convertTransactionItems(data?.items ?? []));
    const { isVisible } = useList(widget, triggerRef);
    const { updateWidgetValue } = useUpdateWidgetValue(widget);
    const { triggerAction, triggerActionsByType } = useTriggerAction(widget);
    const actionEndOfList = actions?.find((a) => a?.actionType === ActionActionTypeEnum.onEndOfList);
    const hasItems = items && Array.isArray(items) && items.length > 0;
    const [showSpinner, toggleSpinner] = useState(true !== paging?.theEnd && !!actionEndOfList && hasItems);
    const onRefreshAction = actions?.find((a) => a?.actionType === ActionActionTypeEnum.onRefresh);

    useSubscribeOnWsUpdate(onRefreshAction);

    const mergeItemsAndUpdateCache = useCallback(
        (currentItems: Array<TConsumerItem>, newItems: Array<TConsumerItem>) => {
            const mergeItems = mergeArrays(convertTransactionItems(currentItems), convertTransactionItems(newItems));
            if (mergeItems.isHasChanges) {
                setItems(mergeItems.items);
                putCache({
                    ...widget?.data,
                    items: mergeItems.items,
                }).catch((e) => console.warn('Cannot update cache...', e));
            }
        },
        [putCache, widget.data],
    );

    useEffect(() => {
        getCache().then((cacheData) => {
            if (cacheData) {
                setItems(convertTransactionItems(cacheData?.items ?? []));
            } else {
                mergeItemsAndUpdateCache([], data.items);
            }
        });
    }, [data.items, getCache, mergeItemsAndUpdateCache]);

    useEffect(() => {
        eventBus.subscribeWS(widget?.internalId || '', (w) => {
            if (!w || !w.data) return;
            if (fetchingData.current) return;

            const cW = w as TWidget<TConsumerListData, TListOption>;
            const newItems = cW?.data?.items;
            if (Array.isArray(newItems) && newItems.length > 0) {
                mergeItemsAndUpdateCache(items, newItems);
            }
        });
        return () => {
            eventBus.unsubscribeWsSpecificWidget(widget.internalId);
        };
    }, [items, mergeItemsAndUpdateCache, widget.internalId]);

    const onTriggerAction = useEffectEvent((action: TWAction) => {
        return triggerAction(action);
    });

    useEffect(() => {
        let timeoutId = timeoutRef?.current;

        async function startFetching() {
            if (showSpinner && actionEndOfList && isVisible && !fetchingData.current) {
                fetchingData.current = true;
                const result = await onTriggerAction(actionEndOfList);
                if (result) {
                    const responseData = result.data;
                    if (Array.isArray(responseData.items) && responseData.items.length > 0) {
                        mergeItemsAndUpdateCache(items, responseData.items);
                    }
                    toggleSpinner(true !== responseData.settings?.paging?.theEnd);
                } else {
                    toggleSpinner(false);
                }
            }
        }

        startFetching()
            .catch((e) => console.warn(e))
            .finally(() => {
                timeoutId = setTimeout(() => (fetchingData.current = false), 500);
            });
        return () => {
            if (timeoutId) {
                clearTimeout(timeoutId);
            }
        };
    }, [showSpinner, items, isVisible, actionEndOfList, mergeItemsAndUpdateCache]);

    const handleClick = (_uid: string, item: TItem) => {
        const value = item?.value;
        updateWidgetValue(value);
        triggerActionsByType(ActionActionTypeEnum.onItemSelected, { value }).then();
    };

    return (
        <div data-test={widget.uid} className={clsx(!hasItems && styles.emptyListContainer)} style={getInlineStyles({ margin: margin })}>
            <ConditionalRender condition={!hasItems}>
                <div className={styles.emptyContainer}>
                    <SvgComponent name={AssetsSvgMap.ic_dotted_list} fill="grey_72_lc" />
                    <TextCommon
                        isWidget={false}
                        content={messages.emptyPlaceholder}
                        options={{
                            variant: 'secondary',
                            textAlign: 'center',
                            textColor: 'grey_72_lc',
                            margin: { top: 16 },
                        }}
                    />
                </div>
            </ConditionalRender>
            <ConditionalRender condition={hasItems}>
                <ListTransactionItems wid={widget.uid} handleClick={handleClick} items={items} messages={messages} />
                <ConditionalRender condition={showSpinner}>
                    <div className={styles.spinnerContainer} ref={triggerRef}>
                        <SpinnerCircle size={'32px'} />
                    </div>
                </ConditionalRender>
            </ConditionalRender>
        </div>
    );
};
